import axios from 'axios';
import useCctvStatus from 'modules/dashboard/hooks/use-cctv-status';
import { useCallback, useEffect } from 'react';
import {
    GridwallStreamSource,
    useGridwallStreamTokenRefresh,
    useStreamTokenRefresh,
} from 'modules/dashboard/hooks/use-stream-token-refresh';

interface UseCctvStatusCheckerProps {
    hlsUrl?: string;
    webrtcUrl?: string;
    projectId?: string;
    cameraId?: string;
    gridwall?: GridwallStreamSource;
}

type UseCctvStatusCheckerInput = string | UseCctvStatusCheckerProps;

const useCctvStatusChecker = (input: UseCctvStatusCheckerInput) => {
    const { isLoading, isOffline, isOnline, setOnline, setOffline, status } = useCctvStatus();

    // Handle backward compatibility: accept string or object
    let hlsUrlProp: string | undefined;
    let webrtcUrlProp: string | undefined;
    let projectId: string | undefined;
    let cameraId: string | undefined;
    let gridwall: GridwallStreamSource | undefined;

    if (typeof input === 'string') {
        hlsUrlProp = input;
    } else {
        hlsUrlProp = input.hlsUrl;
        webrtcUrlProp = input.webrtcUrl;
        projectId = input.projectId;
        cameraId = input.cameraId;
        gridwall = input.gridwall;
    }

    const cameraStream = useStreamTokenRefresh(projectId, cameraId, hlsUrlProp, webrtcUrlProp, !gridwall);
    const gridwallStream = useGridwallStreamTokenRefresh(gridwall, hlsUrlProp, webrtcUrlProp);
    const stream = gridwall ? gridwallStream : cameraStream;

    const {
        hlsUrl: updatedHlsUrl,
        webrtcUrl: updatedWebrtcUrl,
        isLoading: isTokenLoading,
        refetch: refetchToken,
    } = stream;

    // Only use updated URL if token is ready, otherwise wait
    const streamUrl =
        updatedHlsUrl || updatedWebrtcUrl || (projectId || gridwall ? undefined : hlsUrlProp || webrtcUrlProp);

    const checkStatus = useCallback(async () => {
        if (!streamUrl) {
            setOffline();
            return;
        }

        try {
            await axios.get(streamUrl, { timeout: 5000 });
            setOnline();
        } catch (ex) {
            setOffline();

            const errorStatus = (ex as any)?.response?.status;
            if (errorStatus === 401) {
                console.log('Token expired (401), refreshing...');
                refetchToken();
            }
        }
    }, [streamUrl, setOnline, setOffline, refetchToken]);

    // Only check status after token is loaded
    useEffect(() => {
        if (!isTokenLoading && streamUrl) {
            checkStatus();
        }
    }, [streamUrl, checkStatus, isTokenLoading]);

    useEffect(() => {
        if (isTokenLoading) return;

        const intervalId = setInterval(() => {
            checkStatus();
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, [streamUrl, checkStatus, isTokenLoading]);

    return {
        isLoading,
        isOffline,
        isOnline,
        status,
        hlsUrl: updatedHlsUrl,
        webrtcUrl: updatedWebrtcUrl,
    };
};

export default useCctvStatusChecker;
